/**
 * Automatic long-term memory.
 *
 * After a successful assistant reply the client asks a user-selected chat model
 * to extract durable facts from the latest exchange. Extraction is a separate
 * model call (hence the growing token cost), while every resulting memory stays
 * in the local IndexedDB store and is never uploaded as a memory record.
 */
import { memoryRepo } from '../../core/repositories';
import type { ChatMessage, MemoryEntry, MemoryImportance } from '../../core/types';
import type { ProviderAdapter } from '../../core/providers';

export const AUTO_MEMORY_STORAGE_KEY = 'zxtalk.autoMemory.v1';
export const AUTO_MEMORY_CHANGED_EVENT = 'zxtalk:auto-memory-changed';
/** Empty provider id means "follow the current conversation's model". */
export const FOLLOW_CONVERSATION_PROVIDER = '';
export const MAX_AUTO_MEMORIES_PER_TURN = 3;
const MAX_CONTEXT_CHARS = 4000;

export interface AutoMemorySettings {
  enabled: boolean;
  providerId: string;
  model: string;
}

export interface MemoryCandidate {
  content: string;
  tags: string[];
  importance: MemoryImportance;
}

const DEFAULT_SETTINGS: AutoMemorySettings = {
  enabled: false,
  providerId: FOLLOW_CONVERSATION_PROVIDER,
  model: '',
};

function storage(): Storage | undefined {
  try {
    return typeof globalThis.localStorage === 'undefined' ? undefined : globalThis.localStorage;
  } catch {
    return undefined;
  }
}

function normalizeImportance(value: unknown): MemoryImportance {
  const number = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(number)) return 3;
  return Math.min(5, Math.max(1, Math.round(number))) as MemoryImportance;
}

export function normalizeAutoMemorySettings(input: unknown): AutoMemorySettings {
  const raw = (input ?? {}) as Partial<AutoMemorySettings>;
  return {
    enabled: raw.enabled === true,
    providerId: typeof raw.providerId === 'string' ? raw.providerId.trim() : FOLLOW_CONVERSATION_PROVIDER,
    model: typeof raw.model === 'string' ? raw.model.trim() : '',
  };
}

export function loadAutoMemorySettings(): AutoMemorySettings {
  const store = storage();
  if (!store) return { ...DEFAULT_SETTINGS };
  const raw = store.getItem(AUTO_MEMORY_STORAGE_KEY);
  if (raw === null) return { ...DEFAULT_SETTINGS };
  try {
    return normalizeAutoMemorySettings(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveAutoMemorySettings(settings: AutoMemorySettings): AutoMemorySettings {
  const normalized = normalizeAutoMemorySettings(settings);
  storage()?.setItem(AUTO_MEMORY_STORAGE_KEY, JSON.stringify(normalized));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTO_MEMORY_CHANGED_EVENT, { detail: normalized }));
  }
  return normalized;
}

export function updateAutoMemorySettings(patch: Partial<AutoMemorySettings>): AutoMemorySettings {
  return saveAutoMemorySettings({ ...loadAutoMemorySettings(), ...patch });
}

/** Extracts the JSON array even when the model wraps it in prose or fences. */
export function parseMemoryCandidates(text: string): MemoryCandidate[] {
  const start = text.indexOf('[');
  const end = text.lastIndexOf(']');
  if (start < 0 || end <= start) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(text.slice(start, end + 1));
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const candidates: MemoryCandidate[] = [];
  for (const item of parsed) {
    if (!item || typeof item !== 'object') continue;
    const value = item as { content?: unknown; tags?: unknown; importance?: unknown };
    if (typeof value.content !== 'string') continue;
    const content = value.content.trim();
    if (content.length < 2 || content.length > 240) continue;
    const tags = Array.isArray(value.tags)
      ? value.tags
          .filter((tag): tag is string => typeof tag === 'string')
          .map((tag) => tag.trim())
          .filter((tag) => tag !== '')
          .slice(0, 8)
      : [];
    candidates.push({ content, tags, importance: normalizeImportance(value.importance) });
    if (candidates.length >= MAX_AUTO_MEMORIES_PER_TURN) break;
  }
  return candidates;
}

function normalizedContent(value: string): string {
  return value.toLowerCase().replace(/[\s，。！？、,.!?;；:：'"“”‘’()（）[\]【】]/g, '');
}

/** Exact and near-duplicate protection keeps repeated turns from bloating the store. */
export function selectNewCandidates(
  existing: readonly Pick<MemoryEntry, 'content'>[],
  candidates: readonly MemoryCandidate[],
): MemoryCandidate[] {
  const known = existing.map((memory) => normalizedContent(memory.content)).filter((item) => item !== '');
  const selected: MemoryCandidate[] = [];
  for (const candidate of candidates) {
    const normalized = normalizedContent(candidate.content);
    if (normalized === '') continue;
    const duplicate = [...known, ...selected.map((item) => normalizedContent(item.content))].some(
      (item) => item === normalized || (item.length >= 8 && normalized.includes(item)) || (normalized.length >= 8 && item.includes(normalized)),
    );
    if (!duplicate) selected.push(candidate);
    if (selected.length >= MAX_AUTO_MEMORIES_PER_TURN) break;
  }
  return selected;
}

export function buildAutoMemoryMessages(
  recent: readonly Pick<ChatMessage, 'role' | 'content'>[],
  userText: string,
  assistantText: string,
): { system: string; user: string } {
  const context = [...recent, { role: 'user' as const, content: userText }, { role: 'assistant' as const, content: assistantText }]
    .filter((message) => message.role === 'user' || message.role === 'assistant')
    .map((message) => `${message.role === 'user' ? '用户' : '助手'}：${message.content.trim()}`)
    .filter((line) => line !== '用户：' && line !== '助手：')
    .join('\n');
  return {
    system:
      '你是栀歆的记忆整理器。只提取用户长期稳定、以后仍有用的事实、偏好、背景、习惯或约定；忽略寒暄、一次性指令、剧情中的虚构身份和助手的自我描述。' +
      '没有值得长期保存的内容就返回 []。最多 3 条。只输出 JSON 数组，不要 Markdown、解释或代码围栏。' +
      '每项格式：{"content":"一句完整的中文事实","tags":["短标签"],"importance":1到5的整数}。',
    user: context.slice(-MAX_CONTEXT_CHARS),
  };
}

export interface AutoMemoryRunInput {
  adapter: ProviderAdapter;
  model: string;
  conversationId: string;
  recent: readonly Pick<ChatMessage, 'role' | 'content'>[];
  userText: string;
  assistantText: string;
}

function requestMessage(role: 'system' | 'user', content: string): ChatMessage {
  return {
    id: `auto-memory-${role}`,
    conversationId: 'auto-memory',
    role,
    content,
    createdAt: Date.now(),
    status: 'done',
  };
}

async function extract(input: AutoMemoryRunInput): Promise<MemoryEntry[]> {
  const prompt = buildAutoMemoryMessages(input.recent, input.userText, input.assistantText);
  let output = '';
  const iterator = input.adapter.chat({
    messages: [requestMessage('system', prompt.system), requestMessage('user', prompt.user)],
    settings: {
      maxContext: 8192,
      thinkingLevel: 'off',
      temperature: 0.2,
      topP: 0.9,
      maxOutputTokens: 800,
      stream: false,
    },
    model: input.model,
  });
  for await (const chunk of iterator) {
    if (chunk.type === 'text') output += chunk.delta ?? '';
    else if (chunk.type === 'error') throw new Error(chunk.error ?? 'AUTO_MEMORY_FAILED');
    else if (chunk.type === 'done') break;
  }
  const candidates = selectNewCandidates(await memoryRepo.listMemories(), parseMemoryCandidates(output));
  const saved: MemoryEntry[] = [];
  for (const candidate of candidates) {
    saved.push(
      await memoryRepo.createAndSaveMemory({
        content: candidate.content,
        tags: candidate.tags,
        importance: candidate.importance,
        sourceConversationId: input.conversationId,
      }),
    );
  }
  return saved;
}

let queue: Promise<unknown> = Promise.resolve();

/** Serialises background extraction so fast consecutive turns cannot duplicate writes. */
export function enqueueAutoMemory(input: AutoMemoryRunInput): Promise<MemoryEntry[]> {
  const next = queue.then(
    () => extract(input),
    () => extract(input),
  );
  queue = next.catch(() => undefined);
  return next;
}
